import { Checkbox, Popover, Tooltip } from "@arco-design/web-react";
import { IconDown } from "@arco-design/web-react/icon";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ReportType } from "./downloadReports";

const REPORT_TYPE_LABEL_KEY: Record<ReportType, string> = {
  platform: "report_type_platform",
  moderation: "report_type_moderation",
  "user-detail": "report_type_user_detail",
  qr: "report_type_qr",
};

type Props = {
  availableReportTypes: ReportType[];
  selectedReports: ReportType[];
  onChange: (types: ReportType[]) => void;
};

export function ReportsTypePicker({ availableReportTypes, selectedReports, onChange }: Props) {
  const { t } = useTranslation();

  const allSelected =
    availableReportTypes.length > 0 &&
    availableReportTypes.every((type) => selectedReports.includes(type));
  const someSelected = selectedReports.length > 0 && !allSelected;

  const summaryLabel = useMemo(() => {
    if (selectedReports.length === 0) {
      return t("admin.statistics.reports_select_placeholder");
    }
    if (allSelected) {
      return t("admin.statistics.reports_all_selected");
    }
    if (selectedReports.length === 1) {
      return t(`admin.statistics.${REPORT_TYPE_LABEL_KEY[selectedReports[0]]}`);
    }
    return t("admin.statistics.reports_count_selected", { count: selectedReports.length });
  }, [allSelected, selectedReports, t]);

  const toggleType = (type: ReportType, checked: boolean) => {
    if (checked) {
      onChange(selectedReports.includes(type) ? selectedReports : [...selectedReports, type]);
      return;
    }
    onChange(selectedReports.filter((item) => item !== type));
  };

  const menu = (
    <div className="statistics__reports-menu" onClick={(event) => event.stopPropagation()}>
      {availableReportTypes.map((type) => {
        const label = t(`admin.statistics.${REPORT_TYPE_LABEL_KEY[type]}`);
        const labelNode =
          type === "user-detail" ? (
            <Tooltip
              content={t("admin.statistics.report_user_detail_tooltip")}
              position="top"
              trigger={["hover", "focus"]}
            >
              <span>{label}</span>
            </Tooltip>
          ) : (
            label
          );

        return (
          <Checkbox
            key={type}
            checked={selectedReports.includes(type)}
            onChange={(checked) => toggleType(type, checked)}
            className="statistics__reports-menu-item"
          >
            {labelNode}
          </Checkbox>
        );
      })}
      <div className="statistics__reports-menu-divider" />
      <Checkbox
        checked={allSelected}
        indeterminate={someSelected}
        onChange={(checked) => onChange(checked ? [...availableReportTypes] : [])}
        className="statistics__reports-menu-item statistics__reports-menu-item--all"
      >
        {t("admin.statistics.report_select_all")}
      </Checkbox>
    </div>
  );

  return (
    <div className="statistics__reports-popover">
      <Popover content={menu} trigger="click" position="bl">
        <button type="button" className="statistics__reports-trigger" aria-haspopup="listbox">
          <span className="statistics__reports-trigger-label">{summaryLabel}</span>
          <IconDown className="statistics__reports-trigger-icon" />
        </button>
      </Popover>
    </div>
  );
}
